import apiHelper from '../utils/apiHelper';

class InnovationRadarService {
    constructor() {
        this.baseUrl = process.env.REACT_APP_API_URL;
        this.choiceCache = null; // Cache for choice field options
        this.cacheTimestamp = null;
        this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    }

    // Get choice field options dynamically from SharePoint
    async getChoiceFieldOptions() {
        try {
            // Check cache first
            const now = Date.now();
            if (this.choiceCache && this.cacheTimestamp && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
                return this.choiceCache;
            }

            const headers = await apiHelper.getHeaders('graph/innovationradar/choices');
            
            const response = await fetch(`${this.baseUrl}/api/graph/innovationradar/choices`, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                console.warn('Failed to fetch dynamic choice options, falling back to static options');
                return this.getStaticChoiceOptions();
            }

            const choiceOptions = await response.json();
            
            // Cache the results
            this.choiceCache = choiceOptions;
            this.cacheTimestamp = now;
            
            return choiceOptions;
        } catch (error) {
            console.error('Error fetching choice field options:', error);
            console.warn('Falling back to static choice options');
            return this.getStaticChoiceOptions();
        }
    }

    // Get all innovation radar items (topics only)
    async getInnovationRadarItems() {
        try {
            const headers = await apiHelper.getHeaders('graph/innovationradar');
            const response = await fetch(`${this.baseUrl}/api/graph/innovationradar`, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching innovation radar items:', error);
            throw error;
        }
    }

    // Get drivers and types from separate SharePoint list
    async getDriverTypesItems() {
        try {
            const headers = await apiHelper.getHeaders('graph/innovationradar/drivertypes');
            const response = await fetch(`${this.baseUrl}/api/graph/innovationradar/drivertypes`, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching drivers and types:', error);
            throw error;
        }
    }

    // Create new driver or type in separate list
    async createDriverTypeItem(itemData, createdBy = 'Admin') {
        try {
            const headers = await apiHelper.getHeaders('graph/innovationradar/drivertypes');
            
            const payload = {
                Title: itemData.Title,
                Type: itemData.Type, // 'Driver' or 'Type'
                Description: itemData.Description,
                SortOrder: itemData.SortOrder,
                CssClass: itemData.CssClass,
                createdBy: createdBy
            };

            const response = await fetch(`${this.baseUrl}/api/graph/innovationradar/drivertypes`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to create driver/type: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error creating driver/type:', error);
            throw error;
        }
    }

    // Update driver or type in separate list
    async updateDriverTypeItem(id, itemData, updatedBy = 'Admin') {
        try {
            const headers = await apiHelper.getHeaders('graph/innovationradar/drivertypes');
            
            const payload = {
                Title: itemData.Title,
                Type: itemData.Type, // 'Driver' or 'Type'
                Description: itemData.Description,
                SortOrder: itemData.SortOrder,
                CssClass: itemData.CssClass,
                updatedBy: updatedBy
            };

            const response = await fetch(`${this.baseUrl}/api/graph/innovationradar/drivertypes/${id}`, {
                method: 'PUT',
                headers: headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update driver/type: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error updating driver/type:', error);
            throw error;
        }
    }

    // Get innovation radar item by ID
    async getInnovationRadarItemById(id) {
        try {
            const headers = await apiHelper.getHeaders('graph/innovationradar');
            const response = await fetch(`${this.baseUrl}/api/graph/innovationradar/${id}`, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching innovation radar item by ID:', error);
            throw error;
        }
    }

    // Create new innovation radar item
    async createInnovationRadarItem(itemData, createdBy = 'Admin') {
        try {
            const headers = await apiHelper.getHeaders('graph/createInnovationRadar');
            
            const payload = {
                Title: itemData.Title,
                ContentType: itemData.ContentType,
                Description: itemData.Description,
                Category: itemData.Category,
                DriversInnovation: itemData.DriversInnovation,
                PrimaryBenefit: itemData.PrimaryBenefit,
                GroupColour: itemData.GroupColour,
                RankingFlag: itemData.RankingFlag,
                Publish: itemData.Publish,
                // All SharePoint fields
                IndustrySector: itemData.IndustrySector,
                TopicTheme: itemData.TopicTheme,
                ImportanceLevel: itemData.ImportanceLevel,
                UrgencyLevel: itemData.UrgencyLevel,
                TopicLongName: itemData.TopicLongName,
                Definition: itemData.Definition,
                createdBy: createdBy
            };

            const response = await fetch(`${this.baseUrl}/api/graph/createInnovationRadar`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to create innovation radar item: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error creating innovation radar item:', error);
            throw error;
        }
    }

    // Update innovation radar item
    async updateInnovationRadarItem(id, itemData, updatedBy = 'Admin') {
        try {
            const headers = await apiHelper.getHeaders('graph/updateInnovationRadar');
            
            const payload = {
                Title: itemData.Title,
                ContentType: itemData.ContentType,
                Description: itemData.Description,
                Category: itemData.Category,
                DriversInnovation: itemData.DriversInnovation,
                PrimaryBenefit: itemData.PrimaryBenefit,
                GroupColour: itemData.GroupColour,
                RankingFlag: itemData.RankingFlag,
                Publish: itemData.Publish,
                // All SharePoint fields
                IndustrySector: itemData.IndustrySector,
                TopicTheme: itemData.TopicTheme,
                ImportanceLevel: itemData.ImportanceLevel,
                UrgencyLevel: itemData.UrgencyLevel,
                TopicLongName: itemData.TopicLongName,
                Definition: itemData.Definition,
                updatedBy: updatedBy
            };

            const response = await fetch(`${this.baseUrl}/api/graph/updateInnovationRadar/${id}`, {
                method: 'PUT',
                headers: headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update innovation radar item: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error updating innovation radar item:', error);
            throw error;
        }
    }

    // Helper methods for data transformation
    getDrivers(driverTypesData) {
        return driverTypesData.filter(item => item.Type === 'Driver')
                             .sort((a, b) => {
                                 // Sort by SortOrder if available, otherwise by title
                                 const aOrder = parseInt(a.SortOrder) || 999;
                                 const bOrder = parseInt(b.SortOrder) || 999;
                                 if (aOrder !== bOrder) return aOrder - bOrder;
                                 return (a.Title || '').localeCompare(b.Title || '');
                             });
    }

    getTypes(driverTypesData) {
        return driverTypesData.filter(item => item.Type === 'Type')
                             .sort((a, b) => {
                                 // Sort by SortOrder if available, otherwise by title
                                 const aOrder = parseInt(a.SortOrder) || 999;
                                 const bOrder = parseInt(b.SortOrder) || 999;
                                 if (aOrder !== bOrder) return aOrder - bOrder;
                                 return (a.Title || '').localeCompare(b.Title || '');
                             });
    }

    getTopics(topicsData) {
        // All items from the main innovation radar list are topics
        return topicsData.sort((a, b) => {
            // Sort by ranking flag if available, otherwise by title
            const aRank = parseInt(a.RankingFlag) || 999;
            const bRank = parseInt(b.RankingFlag) || 999;
            if (aRank !== bRank) return aRank - bRank;
            return (a.Title || '').localeCompare(b.Title || '');
        });
    }

    // Get content type options
    getContentTypeOptions() {
        return [
            { value: 'Driver', label: 'Driver' },
            { value: 'Type', label: 'Type' },
            { value: 'Topic', label: 'Topic' }
        ];
    }

    // Get category options (for Topics)
    getCategoryOptions() {
        return [
            { value: 'Sustaining/Incremental', label: 'Sustaining/Incremental' },
            { value: 'Adjacent', label: 'Adjacent' },
            { value: 'Emerging/Disruptive', label: 'Emerging/Disruptive' }
        ];
    }

    // Get driver options (for Topics) - based on actual SharePoint choices
    getDriverOptions(drivers) {
        // From SharePoint schema: Business/Operations, Market&Regulation, Product/ServiceMaintenance&Improvement, NewProduct/ServiceDevelopment, Emerging/Disruptive
        return [
            { value: 'Business/Operations', label: 'Business/Operations' },
            { value: 'Market&Regulation', label: 'Market & Regulation' },
            { value: 'Product/ServiceMaintenance&Improvement', label: 'Product/Service Maintenance & Improvement' },
            { value: 'NewProduct/ServiceDevelopment', label: 'New Product/Service Development' },
            { value: 'Emerging/Disruptive', label: 'Emerging/Disruptive' }
        ];
    }

    // Get group color options - based on SharePoint schema (Group1-Group6)
    getGroupColorOptions() {
        return [
            { value: 'Group1', label: 'Group1' },
            { value: 'Group2', label: 'Group2' },
            { value: 'Group3', label: 'Group3' },
            { value: 'Group4', label: 'Group4' },
            { value: 'Group5', label: 'Group5' },
            { value: 'Group6', label: 'Group6' }
        ];
    }

    // Get ranking flag options - based on SharePoint schema (1-42+)
    getRankingFlagOptions() {
        const options = [];
        for (let i = 1; i <= 42; i++) {
            options.push({ value: i.toString(), label: i.toString() });
        }
        return options;
    }

    // Get industry sector options - based on SharePoint schema
    getIndustrySectorOptions() {
        return [
            { value: 'Irish Funds', label: 'Irish Funds' },
            { value: 'Banking', label: 'Banking' },
            { value: 'Insurance', label: 'Insurance' }
        ];
    }

    // Get importance level options - based on SharePoint schema
    getImportanceLevelOptions() {
        return [
            { value: '1 - High Importance', label: '1 - High Importance' },
            { value: '2 - Medium Importance', label: '2 - Medium Importance' },
            { value: '3 - Low - Importance', label: '3 - Low Importance' }
        ];
    }

    // Get urgency level options - based on SharePoint schema
    getUrgencyLevelOptions() {
        return [
            { value: '1 - High Urgency', label: '1 - High Urgency' },
            { value: '2 - Medium Urgency', label: '2 - Medium Urgency' },
            { value: '3 - Low Urgency', label: '3 - Low Urgency' }
        ];
    }

    // Get primary benefit options - based on SharePoint schema
    getPrimaryBenefitOptions() {
        return [
            { value: 'Cost Reduction', label: 'Cost Reduction' },
            { value: 'Cost Avoidance', label: 'Cost Avoidance' },
            { value: 'Improved Productivity', label: 'Improved Productivity' },
            { value: 'Risk Reduction', label: 'Risk Reduction' },
            { value: 'Regulatory Compliance', label: 'Regulatory Compliance' },
            { value: 'Enhanced Customer Experience', label: 'Enhanced Customer Experience' },
            { value: 'Improved Org Effectiveness', label: 'Improved Org Effectiveness' },
            { value: 'Increased Revenue', label: 'Increased Revenue' },
            { value: 'Improved Employee Morale', label: 'Improved Employee Morale' },
            { value: 'Sustainable Business & Planet', label: 'Sustainable Business & Planet' },
            { value: 'Reduce Systemic Risk', label: 'Reduce Systemic Risk' },
            { value: 'Improved Market Economy', label: 'Improved Market Economy' }
        ];
    }

    // Static fallback options when dynamic loading fails
    getStaticChoiceOptions() {
        return {
            industrySector: this.getIndustrySectorOptions(),
            category: this.getCategoryOptions(),
            driversInnovation: this.getDriverOptions(),
            primaryBenefit: this.getPrimaryBenefitOptions(),
            importanceLevel: this.getImportanceLevelOptions(),
            urgencyLevel: this.getUrgencyLevelOptions(),
            rankingFlag: this.getRankingFlagOptions(),
            groupColour: this.getGroupColorOptions()
        };
    }

    // Dynamic wrapper methods that try to use cached options first
    async getDynamicCategoryOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.category || this.getCategoryOptions();
    }

    async getDynamicDriverOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.driversInnovation || this.getDriverOptions();
    }

    async getDynamicIndustrySectorOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.industrySector || this.getIndustrySectorOptions();
    }

    async getDynamicPrimaryBenefitOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.primaryBenefit || this.getPrimaryBenefitOptions();
    }

    async getDynamicImportanceLevelOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.importanceLevel || this.getImportanceLevelOptions();
    }

    async getDynamicUrgencyLevelOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.urgencyLevel || this.getUrgencyLevelOptions();
    }

    async getDynamicRankingFlagOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.rankingFlag || this.getRankingFlagOptions();
    }

    async getDynamicGroupColorOptions() {
        const choices = await this.getChoiceFieldOptions();
        return choices.groupColour || this.getGroupColorOptions();
    }
}

export const innovationRadarService = new InnovationRadarService(); 